import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Check, Ticket, RefreshCw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useDeliveryDriver } from '../DeliveryDriversLayout';
import {
  getDriverInviteCode,
  createDriverInviteCode,
  deactivateDriverInviteCode,
  type OrganizationInviteCode,
} from '@vn-propane/shared';

export default function InviteCodesPage() {
  const { t } = useTranslation(['admin', 'common']);
  const { id: orgId, driverId } = useParams<{ id: string; driverId: string }>();
  const navigate = useNavigate();
  const { driver } = useDeliveryDriver();
  const [inviteCode, setInviteCode] = useState<OrganizationInviteCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Form state for new invite code
  const [showForm, setShowForm] = useState(false);
  const [expiresIn, setExpiresIn] = useState<string>('');

  const driverName = driver?.display_name || driver?.full_name || 'this driver';

  const fetchInviteCode = useCallback(async () => {
    if (!orgId || !driverId) return;

    setLoading(true);
    try {
      const { data, error: apiError, response } = await getDriverInviteCode({
        path: { org_id: orgId, driver_id: driverId },
      });

      if (response.status === 401) {
        navigate('/login');
        return;
      }

      if (apiError) {
        setError(apiError.error?.message || t('inviteCodes.failedToLoadInviteCode'));
        return;
      }

      setInviteCode(data?.data ?? null);
      setError(null);
    } catch (err) {
      setError(t('inviteCodes.failedToLoadInviteCode'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orgId, driverId, navigate, t]);

  useEffect(() => {
    fetchInviteCode();
  }, [fetchInviteCode]);

  const handleGenerate = async () => {
    if (!orgId || !driverId) return;

    setGenerating(true);
    try {
      let expires_at: string | undefined;
      if (expiresIn) {
        const days = parseInt(expiresIn, 10);
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + days);
        expires_at = expiresAt.toISOString();
      }

      const { error: apiError } = await createDriverInviteCode({
        path: { org_id: orgId, driver_id: driverId },
        body: { expires_at },
      });

      if (apiError) {
        setError(apiError.error?.message || t('inviteCodes.failedToGenerateInviteCode'));
        return;
      }

      // Reset form and refresh
      setShowForm(false);
      setExpiresIn('');
      await fetchInviteCode();
    } catch (err) {
      setError(t('inviteCodes.failedToGenerateInviteCode'));
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDeactivate = async () => {
    if (!orgId || !driverId) return;

    try {
      const { error: apiError } = await deactivateDriverInviteCode({
        path: { org_id: orgId, driver_id: driverId },
      });

      if (apiError) {
        setError(apiError.error?.message || t('inviteCodes.failedToDeactivateInviteCode'));
        return;
      }

      await fetchInviteCode();
    } catch (err) {
      setError(t('inviteCodes.failedToDeactivateInviteCode'));
      console.error(err);
    }
  };

  const copyToClipboard = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return t('common:labels.never');
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  const hasActiveCode = inviteCode?.is_active;

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h3 className="text-lg font-semibold">{t('inviteCodes.title')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('inviteCodes.generateForDriver', { driverName })}
        </p>
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-md mb-6">
          {error}
        </div>
      )}

      {hasActiveCode && inviteCode ? (
        <div className="space-y-4">
          <div className="border rounded-lg p-6">
            <div className="text-center mb-4">
              <p className="text-sm text-muted-foreground mb-2">{t('inviteCodes.shareWithDriver')}</p>
              <div className="flex items-center justify-center gap-3">
                <code className="text-3xl font-mono font-bold tracking-widest bg-muted px-6 py-3 rounded-lg">
                  {inviteCode.code}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(inviteCode.code)}
                >
                  {copiedCode ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
            <div className="text-center text-sm text-muted-foreground">
              {t('common:labels.expires')} {formatDate(inviteCode.expires_at)}
            </div>
          </div>

          <div className="flex gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="flex-1">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('inviteCodes.regenerateCode')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('inviteCodes.regenerateTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('inviteCodes.regenerateDescription')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common:buttons.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleGenerate}>
                    {t('inviteCodes.regenerate')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-destructive">
                  {t('inviteCodes.deactivate')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('inviteCodes.deactivateTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('inviteCodes.deactivateDescription')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common:buttons.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeactivate}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {t('inviteCodes.deactivate')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      ) : showForm ? (
        <div className="border rounded-lg p-6 bg-muted/30">
          <h4 className="font-semibold mb-4">{t('inviteCodes.generateInviteCode')}</h4>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="expiresIn">{t('inviteCodes.expiresInDays')}</Label>
              <Input
                id="expiresIn"
                type="number"
                min="1"
                placeholder={t('common:labels.never')}
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t('inviteCodes.leaveEmptyNoExpiration')}
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleGenerate} disabled={generating}>
                {generating ? t('inviteCodes.generating') : t('inviteCodes.generateCode')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setExpiresIn('');
                }}
              >
                {t('common:buttons.cancel')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 border rounded-lg">
          <Ticket className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h4 className="font-semibold mb-2">{t('inviteCodes.noInviteCode')}</h4>
          <p className="text-muted-foreground mb-4">
            {t('inviteCodes.generateForDriverMobile', { driverName })}
          </p>
          <Button onClick={() => setShowForm(true)}>
            {t('inviteCodes.generateInviteCode')}
          </Button>
        </div>
      )}

      {inviteCode && !inviteCode.is_active && (
        <div className="mt-6 p-4 border rounded-lg opacity-60">
          <p className="text-sm text-muted-foreground">
            {t('inviteCodes.previousCodeUsed', { code: inviteCode.code })}
          </p>
        </div>
      )}
    </div>
  );
}
